// "Das läuft gerade bei Hotels" (Signal-Room phase 1, PLAN #13) — weekly market snapshot card.
// Renders nothing when the server returns {card:null} (no complete snapshot, older than 21 days
// Europe/Berlin, or the import kill switch is off) — staleness is decided SERVER-side, the
// client only hides on null. Card content is exporter-defined jsonb; every field is optional
// and rendered defensively.
import { useQuery } from "@tanstack/react-query";
import { TrendingUp, MapPin } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

type MarketFormat = { name?: string; momentum?: string | number; example?: string };
type MarketCardData = {
  weekStart?: string;
  weekNumber?: number;
  card: { headline?: string; sentence?: string; formats?: MarketFormat[] } | null;
  highlights?: Array<Record<string, unknown>> | null;
};

export function HotelMarketCard() {
  const { data } = useQuery<MarketCardData>({
    queryKey: ["/api/signal-room/market-card"],
    staleTime: 5 * 60 * 1000,
  });

  if (!data?.card) return null;
  const { card, weekNumber, highlights } = data;
  const formats = Array.isArray(card.formats) ? card.formats.slice(0, 3) : [];

  return (
    <Card data-testid="card-hotel-market">
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-emerald-600" />
            Das läuft gerade bei Hotels
          </h3>
          {weekNumber != null && (
            <Badge variant="outline" className="text-xs" data-testid="badge-market-week">
              Stand: KW {weekNumber}
            </Badge>
          )}
        </div>
        {card.sentence && <p className="text-sm text-muted-foreground mb-3">{card.sentence}</p>}
        {formats.length > 0 && (
          <ul className="space-y-2 mb-1">
            {formats.map((f, i) => (
              <li key={i} className="text-sm flex items-start gap-2">
                <Badge variant="secondary" className="text-xs shrink-0 mt-0.5">{i + 1}</Badge>
                <span>
                  <span className="font-medium">{f.name ?? "Format"}</span>
                  {f.momentum != null && <span className="text-muted-foreground"> · {String(f.momentum)}</span>}
                  {f.example && <span className="text-muted-foreground block text-xs mt-0.5">{f.example}</span>}
                </span>
              </li>
            ))}
          </ul>
        )}
        {Array.isArray(highlights) && highlights.length > 0 && (
          <div className="mt-3 pt-3 border-t">
            <div className="text-sm font-medium flex items-center gap-1.5 mb-1.5">
              <MapPin className="h-3.5 w-3.5 text-sky-600" />
              Bei euch in der Region
            </div>
            <ul className="space-y-1">
              {highlights.slice(0, 3).map((h, i) => (
                <li key={i} className="text-xs text-muted-foreground">
                  {typeof (h as any).text === "string" ? (h as any).text
                    : typeof (h as any).title === "string" ? (h as any).title
                    : JSON.stringify(h).slice(0, 120)}
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
